import API from '@/api/apiUrls'
import { useApi } from '@/api/useApi'
import { defineStore } from 'pinia'
import { useBaseStore } from '@haskoning/vue-basestore'
import { computed, ref } from 'vue'
import { useAuthorizationStore } from '@/stores/authorization'
import type { CrossSectionType } from '@/stores/type/crossSection.type'
import getUrlQueryParams from '@/api/utils/getUrlQueryParams'
import type { PaginationServer } from '@/stores/type/field.type'
import {
  ASPHALTMATT,
  BLOCKMAT,
  CONCRETE_BLOCK,
  CONCRETE_ELEMENT,
  FILTER_NET,
  GABION,
  INSITU_FILLED_MATTRESS_PROSERVE,
  RUBBLE_MOUND,
  type ConcreteElementType
} from '@/stores/type/material.type'
import { useMaterialStore } from '@/stores/material'

const { api } = useApi()
const baseURL = API.CORE.CROSS_SECTION.INDEX
const exportExcelUrl = API.CORE.CROSS_SECTION.EXPORT_EXCEL
const exportMathCADUrl = API.CORE.CROSS_SECTION.EXPORT_MATHCAD

export const useCrossSectionStore = defineStore('crossSection', () => {
  const authorizationStore = useAuthorizationStore()
  const canChangeCrossSection = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['change_crosssection']
    })
  })

  const defaultPaginationServer = {
    count: 0,
    next: null,
    previous: null,
    results: []
  }

  const baseStore = useBaseStore<CrossSectionType>({ service: baseURL, api })
  const paginationServer = ref<PaginationServer<CrossSectionType>>(defaultPaginationServer)
  const itemsPerPage = ref(10)
  const page = ref(1)
  const exportExcelLoading = ref(false)
  const exportMathCADLoading = ref(false)

  const resetPagination = () => {
    baseStore.items.value = []
    paginationServer.value = defaultPaginationServer
  }

  const needFetchPagination = (nextPage: number): boolean => {
    return nextPage > Math.floor(baseStore.items.value.length / itemsPerPage.value)
  }

  const fetchPaginationItems = async (filter = {}): Promise<CrossSectionType[] | boolean> => {
    baseStore.fetchLoading.value = true
    const filterPagination = {
      ...filter,
      page_size: itemsPerPage.value
    }
    let url = baseURL + getUrlQueryParams(filterPagination)
    if (paginationServer.value.next) {
      url = paginationServer.value.next
    }
    try {
      const response = await api.service(url).get()
      const data = response as PaginationServer<CrossSectionType>
      baseStore.items.value.push(...data.results)
      paginationServer.value = data
    } catch (e) {
      console.error(e)
      baseStore.fetchLoading.value = false
      return false
    }
    baseStore.fetchLoading.value = false
    return baseStore.items.value
  }

  const downloadExcel = async (id: number) => {
    exportExcelLoading.value = true
    try {
      await api.service(exportExcelUrl(id)).downloadBlob()
    } catch (e) {
      console.error(e)
    }
    exportExcelLoading.value = false
  }

  const downloadMathCAD = async (id: number) => {
    exportMathCADLoading.value = true
    try {
      await api.service(exportMathCADUrl(id)).downloadBlob()
    } catch (e) {
      console.error(e)
    }
    exportMathCADLoading.value = false
  }

  function resetAll() {
    baseStore.reset()
    resetPagination()
  }

  const count = computed(() => {
    return paginationServer.value.count
  })

  const pageItems = computed<CrossSectionType[]>(() => {
    const startIndex = itemsPerPage.value * (page.value - 1)
    const endIndex = itemsPerPage.value * page.value
    return baseStore.items.value ? baseStore.items.value.slice(startIndex, endIndex) : []
  })

  const availableInfo = computed(() => {
    const materialStore = useMaterialStore()
    const { currentItem: currentMaterial } = materialStore
    const info = [
      'calculation_method',
      'calculation_type',
      'vessel_name',
      'normative_flow_velocity_at_bed',
      'protection_width',
      'protection_length',
      'total_area',
      'engine'
    ]
    const materialType = currentMaterial?.material_type
    if (materialType?.includes(RUBBLE_MOUND)) {
      info.push('min_required_grading', 'effective_safety_factor', 'average_rock_layer_thickness')
    }
    if (materialType?.includes(GABION)) {
      info.push('d_gabion', 'm50_gabion')
    }
    if (materialType?.includes(CONCRETE_ELEMENT)) {
      const concreteElementType = currentMaterial?.concrete_element_type
      const concreteElementMapping = {
        [BLOCKMAT]: 'd_blockmat',
        [CONCRETE_BLOCK]: 'd_block',
        [ASPHALTMATT]: 'd_mattress',
        [INSITU_FILLED_MATTRESS_PROSERVE]: 'insitu_filled_mattress_thickness'
      }
      for (const key in concreteElementMapping) {
        if (concreteElementType?.includes(key as ConcreteElementType)) {
          info.push(concreteElementMapping[key as ConcreteElementType])
        }
      }
    }
    if (materialType?.includes(FILTER_NET)) {
      info.push(
        'filter_unit',
        'filter_unit_height',
        'filter_unit_diameter',
        'filter_unit_volume',
        'filter_unit_layer_thickness'
      )
    }
    return info
  })

  return {
    ...baseStore,
    itemsPerPage,
    page,
    exportExcelLoading,
    count,
    pageItems,
    canChangeCrossSection,
    availableInfo,
    fetchPaginationItems,
    needFetchPagination,
    resetPagination,
    downloadExcel,
    downloadMathCAD,
    resetAll
  }
})
